import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getApiBaseUrl } from './utils/api';
import { PublicClientApplication, BrowserCacheLocation } from '@azure/msal-browser';

const AuthContext = createContext();

const IMPERSONATION_FLAG_KEY = 'impersonating';
const IMPERSONATION_TOKEN_KEY = 'impersonationToken';
const IMPERSONATION_REFRESH_KEY = 'impersonationRefreshToken';
export const IMPERSONATION_HANDSHAKE_QUERY = 'impersonation=starting';
export const IMPERSONATION_MESSAGE = {
  ready: 'PORTAL_IMPERSONATE_READY',
  tokens: 'PORTAL_IMPERSONATE_TOKENS',
  failed: 'PORTAL_IMPERSONATE_FAILED'
};

function isImpersonationHandshake() {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return params.get('impersonation') === 'starting';
}

function consumeImpersonationHash() {
  if (typeof window === 'undefined') return;
  const rawHash = String(window.location.hash || '').replace(/^#/, '');
  if (!rawHash) return;
  const params = new URLSearchParams(rawHash);
  const impersonateToken = params.get('impersonate');
  const refresh = params.get('refresh');
  if (!impersonateToken) return;
  try {
    sessionStorage.setItem(IMPERSONATION_FLAG_KEY, '1');
    sessionStorage.setItem(IMPERSONATION_TOKEN_KEY, impersonateToken);
    if (refresh) sessionStorage.setItem(IMPERSONATION_REFRESH_KEY, refresh);
  } catch (e) {
    console.error('Failed to store impersonation session:', e);
  }
  window.history.replaceState({}, '', `${window.location.pathname}${window.location.search}`);
}

function isImpersonationSession() {
  if (typeof window === 'undefined') return false;
  try {
    return sessionStorage.getItem(IMPERSONATION_FLAG_KEY) === '1';
  } catch (e) {
    return false;
  }
}

function readStoredAuthTokens() {
  if (typeof window === 'undefined') {
    return { token: null, refreshToken: null, impersonating: false };
  }
  if (isImpersonationHandshake()) {
    return { token: null, refreshToken: null, impersonating: false };
  }
  consumeImpersonationHash();
  const impersonating = isImpersonationSession();
  try {
    if (impersonating) {
      return {
        token: sessionStorage.getItem(IMPERSONATION_TOKEN_KEY),
        refreshToken: sessionStorage.getItem(IMPERSONATION_REFRESH_KEY),
        impersonating: true
      };
    }
  } catch (e) {
    // fall through to localStorage
  }
  return {
    token: localStorage.getItem('token'),
    refreshToken: localStorage.getItem('refreshToken'),
    impersonating: false
  };
}

function persistAuthTokens(nextToken, nextRefreshToken, impersonating) {
  if (typeof window === 'undefined') return;
  if (impersonating) {
    if (nextToken) sessionStorage.setItem(IMPERSONATION_TOKEN_KEY, nextToken);
    else sessionStorage.removeItem(IMPERSONATION_TOKEN_KEY);
    if (nextRefreshToken) sessionStorage.setItem(IMPERSONATION_REFRESH_KEY, nextRefreshToken);
    else sessionStorage.removeItem(IMPERSONATION_REFRESH_KEY);
    sessionStorage.setItem(IMPERSONATION_FLAG_KEY, '1');
    return;
  }
  if (nextToken) localStorage.setItem('token', nextToken);
  else localStorage.removeItem('token');
  if (nextRefreshToken) localStorage.setItem('refreshToken', nextRefreshToken);
  else localStorage.removeItem('refreshToken');
}

function clearAuthTokens(impersonating) {
  if (typeof window === 'undefined') return;
  if (impersonating) {
    sessionStorage.removeItem(IMPERSONATION_FLAG_KEY);
    sessionStorage.removeItem(IMPERSONATION_TOKEN_KEY);
    sessionStorage.removeItem(IMPERSONATION_REFRESH_KEY);
    return;
  }
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
}

const initialAuth = readStoredAuthTokens();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(initialAuth.token);
  const [refreshToken, setRefreshToken] = useState(initialAuth.refreshToken);
  const [isImpersonating, setIsImpersonating] = useState(initialAuth.impersonating);
  const [handshakePending, setHandshakePending] = useState(() => isImpersonationHandshake());
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [onTenantChange, setOnTenantChange] = useState(null);
  const [availableTenants, setAvailableTenants] = useState([]);
  const [hasAllTenantAccess, setHasAllTenantAccess] = useState(false);
  const [privileges, setPrivileges] = useState(null);

  // MSAL instance
  const msalClientRef = React.useRef(null);
  const ensureMsalClient = async () => {
    if (msalClientRef.current) return msalClientRef.current;
    // Fetch MSAL config from backend (template tenant)
    const resp = await fetch(`${getApiBaseUrl()}/api/auth/msal/config`);
    if (!resp.ok) {
      const errorText = await resp.text();
      console.error('MSAL config fetch failed:', resp.status, errorText);
      throw new Error(`Failed to load MSAL config: ${resp.status} ${errorText}`);
    }
    const cfg = await resp.json();
    msalClientRef.current = new PublicClientApplication({
      auth: {
        clientId: cfg.clientId,
        authority: `https://login.microsoftonline.com/${cfg.tenant || 'common'}`,
        redirectUri: 'https://portal.solidit.dk/'
      },
      cache: {
        cacheLocation: BrowserCacheLocation.LocalStorage,
        storeAuthStateInCookie: false
      }
    });
    return msalClientRef.current;
  };

  // Initialize MSAL (required in some versions/environments) and handle any redirect responses
  useEffect(() => {
    const initMsal = async () => {
      try {
        const client = await ensureMsalClient();
        await client.initialize?.();
        // Handle redirect response if any (even though we're using popup, this is safe)
        await client.handleRedirectPromise?.();
        const accounts = client.getAllAccounts();
        if (accounts && accounts.length > 0) {
          client.setActiveAccount(accounts[0]);
        }
      } catch (e) {
        // Non-fatal for popup flow
        // eslint-disable-next-line no-console
        console.error('MSAL init error:', e);
      }
    };
    initMsal();
  }, []);

  // Receive impersonation tokens from the admin window (same-origin postMessage)
  useEffect(() => {
    if (!handshakePending) return undefined;
    const origin = window.location.origin;
    if (window.opener) {
      window.opener.postMessage({ type: IMPERSONATION_MESSAGE.ready }, origin);
    }

    const onMessage = (event) => {
      if (event.origin !== origin) return;
      if (event.data?.type === IMPERSONATION_MESSAGE.failed) {
        setHandshakePending(false);
        setLoading(false);
        return;
      }
      if (event.data?.type !== IMPERSONATION_MESSAGE.tokens || !event.data.token) return;
      persistAuthTokens(event.data.token, event.data.refreshToken || '', true);
      window.history.replaceState({}, '', window.location.pathname || '/');
      setIsImpersonating(true);
      setHandshakePending(false);
      setToken(event.data.token);
      setRefreshToken(event.data.refreshToken || null);
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [handshakePending]);

  // Logout function - defined early so it can be used by refreshAccessToken
  const logout = useCallback(() => {
    const impersonating = isImpersonationSession();
    setUser(null);
    setToken(null);
    setRefreshToken(null);
    setSelectedTenant(null);
    setAvailableTenants([]);
    setHasAllTenantAccess(false);
    setPrivileges(null);
    setIsImpersonating(false);
    clearAuthTokens(impersonating);
    if (impersonating) {
      window.close();
    }
  }, []);

  // Function to refresh access token
  const refreshAccessToken = useCallback(async () => {
    if (!refreshToken) {
      return false;
    }

    try {
      const response = await fetch(`${getApiBaseUrl()}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
      });

      if (response.ok) {
        const data = await response.json();
        setToken(data.token);
        setRefreshToken(data.refreshToken);
        persistAuthTokens(data.token, data.refreshToken, isImpersonationSession());
        if (data.user) {
          setUser((prev) => ({ ...(prev || {}), ...data.user }));
          if (data.user.privileges) {
            setPrivileges(data.user.privileges);
          } else {
            setPrivileges({ privileges: {} });
          }
        }
        return true;
      } else {
        // Refresh token is invalid, logout user
        logout();
        return false;
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      logout();
      return false;
    }
  }, [refreshToken, logout]);

  // Verify token on app load
  useEffect(() => {
    const verifyToken = async () => {
      if (handshakePending && !token) {
        return;
      }
      if (token) {
        try {
          const response = await fetch(`${getApiBaseUrl()}/api/auth/verify`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            setUser(data.user);
            setPrivileges(data.user.privileges || { privileges: {} });
          } else if (response.status === 401) {
            // Token expired, try to refresh
            const refreshed = await refreshAccessToken();
            if (refreshed) {
              // Retry verification with new token
              const newResponse = await fetch(`${getApiBaseUrl()}/api/auth/verify`, {
                headers: {
                  'Authorization': `Bearer ${readStoredAuthTokens().token}`
                }
              });
              if (newResponse.ok) {
                const data = await newResponse.json();
                setUser(data.user);
                setPrivileges(data.user.privileges || { privileges: {} });
              }
            }
          } else {
            clearAuthTokens(isImpersonationSession());
            setIsImpersonating(false);
            setToken(null);
            setRefreshToken(null);
          }
        } catch (error) {
          console.error('Token verification failed:', error);
          clearAuthTokens(isImpersonationSession());
          setIsImpersonating(false);
          setToken(null);
          setRefreshToken(null);
        }
      }
      setLoading(false);
    };

    verifyToken();
  }, [token, refreshToken, refreshAccessToken, handshakePending]);

  const login = async (username, password) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (response.ok) {
        setUser(data.user);
        setPrivileges(data.user.privileges || { privileges: {} });
        setToken(data.token);
        setRefreshToken(data.refreshToken);
        persistAuthTokens(data.token, data.refreshToken, false);
        setIsImpersonating(false);
        return { success: true };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  // Login with Microsoft: acquire an ID token, send to backend /api/auth/msal to exchange for portal JWT
  const loginWithMsal = async () => {
    try {
      const client = await ensureMsalClient();
      // Prefer popup to avoid full-page redirects
      const loginResponse = await client.loginPopup({
        scopes: ['openid', 'profile', 'email']
      });
      const account = loginResponse.account || client.getActiveAccount() || (client.getAllAccounts()[0] || null);
      if (account) {
        client.setActiveAccount(account);
      }

      // Get ID token silently when possible
      const tokenResp = await client.acquireTokenSilent({
        scopes: ['openid', 'profile', 'email'],
        account: account || undefined
      }).catch(async () => {
        return client.acquireTokenPopup({ scopes: ['openid', 'profile', 'email'] });
      });

      const idToken = tokenResp.idToken;
      if (!idToken) {
        return { success: false, error: 'Failed to acquire ID token' };
      }

      const response = await fetch(`${getApiBaseUrl()}/api/auth/msal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idToken })
      });
      const data = await response.json();
      if (!response.ok) {
        return { success: false, error: data.error || 'MSAL login failed' };
      }

      setUser(data.user);
      setPrivileges(data.user.privileges || { privileges: {} });
      setToken(data.token);
      setRefreshToken(data.refreshToken);
      persistAuthTokens(data.token, data.refreshToken, false);
      setIsImpersonating(false);
      return { success: true };
      } catch (err) {
        console.error('MSAL login error:', err);
        if (err.errorCode === 'AADSTS500113') {
          return { success: false, error: 'Azure AD app needs redirect URI configured. Please add this URL to your Azure AD app registration: https://portal.solidit.dk/' };
        }
        return { success: false, error: 'Microsoft sign-in failed: ' + (err.errorMessage || err.message || 'Unknown error') };
      }
  };


  const updateSelectedTenant = (tenant) => {
    setSelectedTenant(tenant);
    if (onTenantChange) {
      onTenantChange(tenant);
    }
  };

  // Utility function for making authenticated API calls with automatic token refresh
  const authenticatedFetch = useCallback(async (url, options = {}) => {
    let response = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${token}`
      }
    });

    // If token expired, try to refresh and retry
    if (response.status === 401) {
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        // Retry with new token
        response = await fetch(url, {
          ...options,
          headers: {
            ...options.headers,
            'Authorization': `Bearer ${readStoredAuthTokens().token || token}`
          }
        });
      }
    }

    return response;
  }, [token, refreshAccessToken]);

  const refreshPrivileges = useCallback(async () => {
    const currentToken = readStoredAuthTokens().token || token;
    if (!currentToken) return false;

    try {
      const response = await fetch(`${getApiBaseUrl()}/api/auth/verify`, {
        headers: {
          Authorization: `Bearer ${currentToken}`
        }
      });

      if (!response.ok) return false;

      const data = await response.json();
      if (data.user) {
        setUser((prev) => ({ ...(prev || {}), ...data.user }));
        setPrivileges(data.user.privileges || { privileges: {} });
      }
      return true;
    } catch (error) {
      console.error('Failed to refresh privileges:', error);
      return false;
    }
  }, [token]);

  // Populate tenants globally and auto-select when needed
  useEffect(() => {
    const loadTenants = async () => {
      if (!user || !token) return;
      try {
        const resp = await authenticatedFetch(`${getApiBaseUrl()}/api/tenants`);
        if (!resp.ok) {
          setHasAllTenantAccess(false);
          return;
        }
        const data = await resp.json();
        if (Array.isArray(data.tenants)) {
          setAvailableTenants(data.tenants);
          setHasAllTenantAccess(Boolean(data.hasAllAccess));
          if (!selectedTenant && data.tenants.length > 0) {
            // Auto-select the only tenant or the first in list
            setSelectedTenant(data.tenants[0]);
            if (onTenantChange) onTenantChange(data.tenants[0]);
          }
        } else {
          setHasAllTenantAccess(false);
        }
      } catch (e) {
        // ignore for now
        setHasAllTenantAccess(false);
      }
    };
    loadTenants();
  }, [user, token, authenticatedFetch, onTenantChange, selectedTenant]);

  // Helper function to check if user has a specific privilege
  const hasPrivilege = useCallback((section, action = 'view') => {
    if (!privileges || !privileges.privileges) {
      return false;
    }

    const sectionPrivileges = privileges.privileges[section];
    if (!sectionPrivileges) {
      return false;
    }

    return sectionPrivileges[action] === true;
  }, [privileges]);

  const value = {
    user,
    token,
    login,
    loginWithMsal,
    logout,
    loading,
    isAuthenticated: !!user,
    isImpersonating,
    selectedTenant,
    setSelectedTenant: updateSelectedTenant,
    setOnTenantChange,
    availableTenants,
    setAvailableTenants,
    hasAllTenantAccess,
    authenticatedFetch,
    authenticatedFetch: authenticatedFetch,
    privileges,
    hasPrivilege,
    refreshPrivileges
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
